import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Warning, Fire } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-73 — Factory reset confirm modal.
 *
 * Wipes every tenant-scoped collection while preserving super_admins,
 * their org(s), and platform config. Meant to be used ONCE right before
 * the first real client goes live. Backend requires an exact confirm
 * string; the modal also requires the operator to type it.
 */

const CONFIRM_STRING = "WIPE_ALL_EXCEPT_SUPER_ADMIN";

export default function FactoryResetDialog({ open, onClose, onReset }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typed, setTyped] = useState("");
  const [keepAudit, setKeepAudit] = useState(true);
  // Iter-73.3 — default TRUE = production semantics: also wipes the seeded
  // QA test canaries (test-employee / test-mediabuyer / test-buyer /
  // test-vendor) and preserves every REAL employee (all internal-org users).
  // Uncheck to fall back to Iter-73.2 staging behavior where the 4 canaries
  // survive the reset.
  const [wipeTests, setWipeTests] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTyped("");
    setPreview(null);
    setLoading(true);
    api.post("/admin/factory-reset", {
      confirm: CONFIRM_STRING,
      dry_run: true,
      keep_audit_log: keepAudit,
      wipe_test_accounts: wipeTests,
    })
      .then((r) => setPreview(r.data))
      .catch((e) => {
        toast.error(
          e?.response?.data?.detail?.message
          || e?.response?.data?.detail
          || "Failed to load impact",
        );
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [open, keepAudit, wipeTests, onClose]);

  const canRun = typed === CONFIRM_STRING;

  const doReset = async () => {
    if (!canRun) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/factory-reset", {
        confirm: CONFIRM_STRING,
        dry_run: false,
        keep_audit_log: keepAudit,
        wipe_test_accounts: wipeTests,
      });
      const total = Object.values(r.data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Factory reset complete — ${total} rows deleted.`);
      onReset?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.message || JSON.stringify(detail) || "Factory reset failed";
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  if (!open) return null;

  const wouldDelete = Object.entries(preview?.would_delete || {});
  const total = wouldDelete.reduce((a, [, v]) => a + v, 0);
  const keptOrgs = preview?.preserved?.kept_org_ids?.length ?? 0;
  const keptEmployees = preview?.preserved?.employee_ids?.length ?? 0;
  const keptSupers = preview?.preserved?.super_admin_ids?.length ?? 0;
  const keptCanaries = preview?.preserved?.test_user_ids?.length ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 md:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="factory-reset-modal"
    >
      <div className="brut-card bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b-4 border-black flex items-start justify-between gap-3 bg-rose-100">
          <div className="flex items-start gap-3">
            <Fire size={28} weight="fill" className="text-rose-800 mt-0.5" />
            <div>
              <div className="label-mono">DANGER — ONE-SHOT RESET</div>
              <h2 className="font-chivo font-black text-lg md:text-xl uppercase tracking-tighter">
                Factory reset the entire platform
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="factory-reset-close"
            className="border border-black px-2 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white"
          >
            <X size={10} weight="bold" /> Cancel
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-start gap-2 text-xs text-rose-900 bg-rose-100 border border-rose-800 p-3">
            <Warning size={16} weight="fill" />
            <div>
              <b>This deletes every tenant-scoped row on the platform.</b>
              {wipeTests ? (
                <>
                  {" "}Every <b>real employee</b> (all internal-org users,
                  regardless of role) survives — you delete those manually
                  from the Employees page. Fake test canaries, every
                  client, every affiliate, every lead, campaign, and
                  destination are wiped. A 24 h undo snapshot writes
                  before any delete.
                </>
              ) : (
                <>
                  {" "}QA canary accounts + their test destination survive
                  the wipe (staging mode). Uncheck the box below to also
                  nuke them.
                </>
              )}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={wipeTests}
              onChange={(e) => setWipeTests(e.target.checked)}
              data-testid="factory-reset-wipe-tests"
              className="w-4 h-4"
            />
            <span className="text-xs">
              <b>Also wipe QA test canary accounts</b>
              <span className="text-zinc-500 ml-1">
                (test-employee, test-mediabuyer, test-buyer, test-vendor
                + the seeded test destination — recommended for production)
              </span>
            </span>
          </label>

          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={keepAudit}
              onChange={(e) => setKeepAudit(e.target.checked)}
              data-testid="factory-reset-keep-audit"
              className="w-4 h-4"
            />
            <span className="text-xs">
              Keep <code className="font-mono">audit_log</code> across the reset
              <span className="text-zinc-500 ml-1">(recommended — full traceability of the wipe)</span>
            </span>
          </label>

          {loading ? (
            <div className="label-mono text-zinc-500">Computing preview…</div>
          ) : (
            <div>
              <div className="label-mono mb-2">
                Will delete {total} row{total === 1 ? "" : "s"} across
                {" "}{wouldDelete.length} collection{wouldDelete.length === 1 ? "" : "s"};
                keeping {keptOrgs} org{keptOrgs === 1 ? "" : "s"}
                {keptEmployees > 0 && (
                  <> + <b>{keptEmployees} real employee{keptEmployees === 1 ? "" : "s"}</b></>
                )}
                {" "}+ {keptSupers} super_admin{keptSupers === 1 ? "" : "s"}
                {keptCanaries > 0 && (
                  <> + <b>{keptCanaries} QA test account{keptCanaries === 1 ? "" : "s"}</b> (staging mode)</>
                )}.
              </div>
              <div className="border border-black max-h-64 overflow-y-auto" data-testid="factory-reset-preview">
                {wouldDelete
                  .sort(([, a], [, b]) => b - a)
                  .map(([k, v]) => (
                    <div
                      key={k}
                      className="grid grid-cols-[1fr_auto] text-xs border-b border-zinc-200 last:border-b-0"
                      data-testid={`preview-row-${k}`}
                    >
                      <div className="p-2 font-mono text-[11px]">{k.replace(/_/g, " ")}</div>
                      <div className="p-2 font-mono font-bold text-right">{v}</div>
                    </div>
                  ))}
                {wouldDelete.length === 0 && (
                  <div className="p-3 text-xs text-zinc-500">Nothing to delete — DB is already clean.</div>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="label-mono">
              Type <span className="font-mono">{CONFIRM_STRING}</span> exactly to unlock the reset:
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={CONFIRM_STRING}
              data-testid="factory-reset-confirm-input"
              className="w-full border border-black p-2 mt-1 font-mono text-xs"
              autoComplete="off"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t-4 border-black p-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={doReset}
            disabled={!canRun || running || loading || total === 0}
            data-testid="factory-reset-run"
            className="bg-rose-700 text-white px-4 py-2 text-xs uppercase tracking-widest border border-black inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Fire size={12} weight="bold" />
            {running ? "Wiping…" : "WIPE EVERYTHING NOW"}
          </button>
        </div>
      </div>
    </div>
  );
}
